import { HttpResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { DriverApiService } from '@fleet/api/driver/driver-api.service';
import { NetworkGroupApiService } from '@fleet/api/network-group/network-group-api.service';
import { NetworkApiService } from '@fleet/api/network/network-api.service';

import { AuthService } from '@fleet/auth/services/auth.service';
import { LocaleService } from '@fleet/locale/locale-service/locale.service';
import {
  ApiResponse,
  AreaModel,
  NetworkGroupModel,
  NetworkModel,
  ReviewModel,
} from '@fleet/model';
import { FleetNavigationService } from '@fleet/navigation';
import { ProductConfigurationService } from '@fleet/product-configuration';
import { NetworkGroupHeaderService } from '@fleet/token';
import { TranslocoService } from '@jsverse/transloco';
import { Settings } from 'luxon';
import { BehaviorSubject, combineLatest, forkJoin, Observable } from 'rxjs';
import { distinctUntilChanged, filter, map, take, tap } from 'rxjs/operators';

interface NetworkGroupCache {
  [key: string]: NetworkGroupModel;
}

interface ServiceAreaCache {
  [key: string]: AreaModel;
}

@Injectable({
  providedIn: 'root',
})
export class NetworkGroupService {
  selectedGroup: BehaviorSubject<NetworkGroupModel> = new BehaviorSubject(null);
  outstandingDriverReviews: BehaviorSubject<ReviewModel[]> =
    new BehaviorSubject([]);
  serviceArea: BehaviorSubject<AreaModel> = new BehaviorSubject(null);
  fleetProduct: string;
  // New properties for network and available groups
  private network = new BehaviorSubject<NetworkModel>(null);
  private availableGroups = new BehaviorSubject<NetworkGroupModel[]>([]);

  // Cache for network groups and service areas
  private networkGroupCache: NetworkGroupCache = {};
  private serviceAreaCache: ServiceAreaCache = {};
  private networkCache: { [key: string]: NetworkModel } = {};

  // Track whether the network group has been initialized
  private networkGroupInitialized = new BehaviorSubject<boolean>(false);
  private aggregator = false;
  attributionNetworkGroup = new BehaviorSubject<NetworkGroupModel>(null);
  private attributionEntity = new BehaviorSubject<string>(null);

  constructor(
    private driverApiService: DriverApiService,
    private networkGroupApiService: NetworkGroupApiService,
    private networkApiService: NetworkApiService,
    private authService: AuthService,
    private productConfigurationService: ProductConfigurationService,
    private localeService: LocaleService,
    private translocoService: TranslocoService,
    private fleetNavigationService: FleetNavigationService,
    private networkGroupHeaderService: NetworkGroupHeaderService,
    @Inject('env') env: any
  ) {
    this.fleetProduct = env.fleetProduct;
    // Set up a listener for when both network is set and network group is initialized
    combineLatest([
      this.network.asObservable().pipe(filter((network) => !!network)),
      this.networkGroupInitialized
        .asObservable()
        .pipe(filter((initialized) => initialized)),
    ]).subscribe(([network, _]) => {
      this.searchServiceAreas();
    });

    // lets initate the network and network group from the user and product configuration
    if (env.fleetProduct !== 'TRAVELLER') {
      combineLatest([
        this.productConfigurationService.networkConfig$,
        this.authService.user$.pipe(
          filter((user) => !!user),
          take(1)
        ),
      ]).subscribe(([networkConfig, user]) => {
        if (networkConfig.networkId) {
          this.getNetwork(networkConfig.networkId);
        }
        if (networkConfig.networkGroupId) {
          this.getNetworkGroup(
            networkConfig.networkGroupId,
            networkConfig.networkId,
            false
          );
        } else {
          if (this.fleetProduct === 'HUB') {
            this.aggregator = true;
          }
          this.setSelectedGroup(null);
        }
      });
    } else {
      //lets get the network from traveller based off product configuration
      this.productConfigurationService.networkConfig$.subscribe(
        (networkConfig) => {
          if (networkConfig && networkConfig.networkId) {
            this.getNetwork(networkConfig.networkId);
          }
          if (networkConfig && networkConfig.networkGroupId) {
            this.getNetworkGroup(
              networkConfig.networkGroupId,
              networkConfig.networkId,
              false
            );
          } else {
            this.setSelectedGroup(null);
          }
        }
      );
    }

    // Subscribe to selected group changes to update the state
    this.selectedGroup
      .pipe(
        tap((group) => {
          if (this.fleetProduct === 'HUB') {
            this.networkGroupHeaderService.networkGroupId.set(
              group?.networkGroupId || null
            );
          }
        })
      )
      .subscribe();

    // Subscribe to attribution group changes to update the state
    this.attributionNetworkGroup
      .pipe(
        tap((group) => {
          if (group) {
            if (this.fleetProduct === 'HUB') {
              this.networkGroupHeaderService.networkGroupId.set(
                group.networkGroupId
              );
            }
          }
        })
      )
      .subscribe();

    //subscribe to set locale and timezone
    this.initializedNetworkAndNetworkGroup$.subscribe(
      ({ networkGroup, network }) => {
        const locale = networkGroup?.locale || network?.locale;
        const lang = locale.replace(/-/g, '_').toLowerCase();
        const timezone = networkGroup?.timezone;
        if (this.translocoService.getActiveLang() !== lang) {
          this.translocoService.setActiveLang(lang);
        }
        if (this.localeService.getLocale() !== locale) {
          this.localeService.setLocale(locale);
        }
        if (timezone) {
          Settings.defaultZone = timezone;
        }
        if (locale) {
          Settings.defaultLocale = locale;
        }
      }
    );
  }

  getNetwork(networkId: string) {
    // Check cache first before making API call
    if (this.networkCache[networkId]) {
      this.network.next(this.networkCache[networkId]);
      return;
    }

    this.networkApiService.getNetwork(networkId).subscribe({
      next: (resp: ApiResponse<NetworkModel>) => {
        // Cache the network data
        this.networkCache[networkId] = resp.data;
        this.network.next(resp.data);
      },
    });
  }

  get networkId() {
    return this.network.value?.networkId;
  }

  getNetworkGroupOrNetworkHeaderId() {}

  get attributionNetworkGroup$() {
    return this.attributionNetworkGroup.asObservable();
  }

  get attributionEntityAndGroup$() {
    return combineLatest([
      this.attributionEntity.asObservable(),
      this.attributionNetworkGroup$,
    ]);
  }

  setSelectedGroup(group: NetworkGroupModel) {
    if (group) {
      if (this.fleetProduct === 'HUB') {
        this.fleetNavigationService.setCurrentGroupByNetworkGroup(group);
      }

      if (group.type === 'TAXI_NETWORK') {
        // Service areas will be searched when both network is set and group is initialized
        // This is now handled by the combineLatest in the constructor
      } else {
        // Clear service area if not a taxi network
        this.serviceArea.next(null);
      }
    } else {
      if (this.fleetProduct === 'HUB') {
        this.fleetNavigationService.setCurrentGroup(null);
      }

      // Handle null group - reset to defaults
      this.serviceArea.next(null);
    }

    // Always update the group and initialization state
    this.selectedGroup.next(group);
    this.networkGroupInitialized.next(true);
  }

  // Search for service areas based on current network group
  private searchServiceAreas() {
    this.serviceArea.next(null);
    const group = this.selectedGroup.value;
    const networkId = this.network.value?.networkId;

    if (!networkId) {
      return;
    }

    // Create a cache key for service area
    const cacheKey = `${networkId}${
      group?.networkGroupId ? '-' + group.networkGroupId : ''
    }`;

    // Check if service area is already in cache
    if (this.serviceAreaCache[cacheKey]) {
      this.serviceArea.next(this.serviceAreaCache[cacheKey]);
      return;
    }

    this.networkApiService
      .searchServiceAreas(networkId, {
        ...(group?.networkGroupId && { networkGroupId: group.networkGroupId }),
        includePolygon: true,
      })
      .subscribe({
        next: (resp: HttpResponse<ApiResponse<AreaModel[]>> | any) => {
          const serviceArea =
            resp.body.data.length > 0 ? resp.body.data[0] : null;

          // Cache the service area
          if (serviceArea) {
            this.serviceAreaCache[cacheKey] = serviceArea;
          }

          this.serviceArea.next(serviceArea);
        },
        error: (err: any) => {
          console.error('Error fetching service areas:', err);
          this.serviceArea.next(null);
        },
      });
  }

  getNetworkGroup(
    networkGroupId: string,
    networkId: string,
    isAttribution: boolean
  ) {
    // Create a cache key for the network group
    const cacheKey = `${networkId}-${networkGroupId}`;

    // Check if network group is already in cache
    if (this.networkGroupCache[cacheKey]) {
      if (isAttribution) {
        this.attributionNetworkGroup.next(this.networkGroupCache[cacheKey]);
      } else {
        this.setSelectedGroup(this.networkGroupCache[cacheKey]);
      }
      return;
    }

    this.networkGroupApiService
      .getNetworkGroup(networkGroupId, networkId)
      .subscribe({
        next: (resp: ApiResponse<NetworkGroupModel>) => {
          // Cache the network group
          this.networkGroupCache[cacheKey] = resp.data;
          if (isAttribution) {
            this.attributionNetworkGroup.next(resp.data);
          } else {
            this.setSelectedGroup(resp.data);
          }
        },
      });
  }

  // Clear cache for specific network group or all if no params provided
  clearNetworkGroupCache(networkId?: string, networkGroupId?: string) {
    if (networkId && networkGroupId) {
      const cacheKey = `${networkId}-${networkGroupId}`;
      delete this.networkGroupCache[cacheKey];
    } else {
      this.networkGroupCache = {};
    }
  }

  // Clear cache for specific service area or all if no params provided
  clearServiceAreaCache(networkId?: string, networkGroupId?: string) {
    if (networkId) {
      const cacheKey = `${networkId}${
        networkGroupId ? '-' + networkGroupId : ''
      }`;
      delete this.serviceAreaCache[cacheKey];
    } else {
      this.serviceAreaCache = {};
    }
  }

  // Clear all caches
  clearAllCaches() {
    this.networkGroupCache = {};
    this.serviceAreaCache = {};
    this.networkCache = {};
  }

  get networkGroup$() {
    return this.selectedGroup.asObservable();
  }

  // Observable that only emits when network group is initialized
  get initializedNetworkAndNetworkGroup$(): Observable<{
    network: NetworkModel;
    networkGroup: NetworkGroupModel;
  }> {
    return combineLatest([
      this.selectedGroup.asObservable(),
      this.networkGroupInitialized.asObservable(),
      this.network.asObservable(),
    ]).pipe(
      filter(([_, initialized, network]) => {
        return initialized == true && network != null;
      }),
      map(([networkGroup, _, network]) => ({ networkGroup, network })),
      distinctUntilChanged((prev, curr) => {
        return (
          prev.networkGroup?.networkGroupId ===
            curr.networkGroup?.networkGroupId &&
          prev.network?.networkId === curr.network?.networkId
        );
      })
    );
  }

  // Observable that emits when network has a value, initialized is true,
  // and includes the current networkGroup (which can be null)
  // Now also includes serviceArea and waits for it to be available
  get initializedNetworkState$(): Observable<{
    network: NetworkModel;
    networkGroup: NetworkGroupModel | null;
    serviceArea: AreaModel | null;
  }> {
    return combineLatest([
      this.network.asObservable().pipe(filter((network) => !!network)),
      this.networkGroupInitialized
        .asObservable()
        .pipe(filter((initialized) => initialized)),
      this.selectedGroup.asObservable(),
      this.serviceArea
        .asObservable()
        .pipe(filter((serviceArea) => !!serviceArea)),
    ]).pipe(
      // Wait for the service area to be loaded (even if it's null)

      map(([network, _, networkGroup, serviceArea]) => ({
        network,
        networkGroup,
        serviceArea,
      }))
    );
  }

  // Check if network group has been initialized
  get isNetworkGroupInitialized(): boolean {
    return this.networkGroupInitialized.value;
  }

  // Observable for initialization state
  get networkGroupInitialized$(): Observable<boolean> {
    return this.networkGroupInitialized.asObservable();
  }

  get networkGroupId() {
    return this.selectedGroup.value
      ? this.selectedGroup.value.networkGroupId
      : null;
  }

  get regionCode() {
    return this.selectedGroup.value
      ? this.selectedGroup.value.regionCode
      : null;
  }

  get serviceArea$() {
    return this.serviceArea.asObservable();
  }

  get networkGroupAndServiceArea$() {
    return combineLatest([this.networkGroup$, this.serviceArea$]).pipe(
      map(([group, area]) => {
        return { networkGroup: group, serviceArea: area };
      })
    );
  }

  get networkGroupAndServiceArea() {
    return {
      networkGroup: this.selectedGroup.value,
      serviceArea: this.serviceArea.value,
    };
  }

  get searchLatLngAndCountryCode$() {
    return this.networkGroupAndServiceArea$.pipe(
      map((groupAndArea: any) => {
        if (groupAndArea.networkGroup && groupAndArea.serviceArea) {
          return {
            latitude: groupAndArea.serviceArea.point.latitude,
            longitude: groupAndArea.serviceArea.point.longitude,

            countryCode: groupAndArea.networkGroup.countryCode,
          };
        } else {
          return null;
        }
      })
    );
  }

  fetchGroupState(networkGroupId: string, networkId: string) {
    const calls = [
      this.driverApiService.searchReviews({
        networkGroupId: networkGroupId,
        networkId: networkId,
        status: 'PENDING,IN_PROGRESS',
      }),
    ];
    forkJoin(calls).subscribe({
      next: ([reviewResp]) => {
        this.outstandingDriverReviews.next(reviewResp.data);
      },
    });
  }

  get networkGroupTimezone() {
    return this.selectedGroup.value && this.selectedGroup.value.timezone
      ? this.selectedGroup.value.timezone
      : null;
  }

  // Getters for new observables
  get network$(): Observable<NetworkModel> {
    return this.network.asObservable();
  }

  get availableGroups$(): Observable<NetworkGroupModel[]> {
    return this.availableGroups.asObservable();
  }

  get hasAvailableGroups$(): Observable<boolean> {
    return this.availableGroups.pipe(
      map((groups) => groups && groups.length > 0)
    );
  }

  setAttributionNetworkGroup(
    networkId: string,
    networkGroupId: string,
    entity: string
  ) {
    if (this.aggregator) {
      this.attributionEntity.next(entity);
      this.getNetworkGroup(networkGroupId, networkId, true);
    }
  }

  setAttributionGroup(networkGroupId: string, networkId: string) {
    this.attributionNetworkGroup.next({
      networkGroupId,
      networkId,
    } as NetworkGroupModel);
  }

  clearGroupIfAggregator() {
    if (this.fleetProduct === 'HUB' && this.aggregator) {
      this.attributionEntity.next(null);
      this.attributionNetworkGroup.next(null);
    }
  }
}
